'use client'

import router, { useRouter } from 'next/router'
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google'
import * as Sentry from '@sentry/nextjs'
import { toast } from 'react-hot-toast'

import { COOKIE_KEYS } from '~/constants/cookie'
import { env } from '~/env'
import { OpenAPI } from '~/generated/backendProvider'
import { postAuthByProviderNameOneTap } from '~/generated/client'
import { setCookie } from '~/utils/cookie'
import {
  getCurrentClientSideHost,
  INITIAL_PAGE_AFTER_LOGIN,
} from '~/utils/global'
import { ANALYTICS } from '~/utils/tracking'

const noop = () => {}

// eslint-disable-next-line @typescript-eslint/no-namespace, @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
declare namespace global {
  interface Window {
    google: unknown
  }
}

const GOOGLE_CLIENT_ID = env.NEXT_PUBLIC_GOOGLE_CLIENT_ID

export const useGoogleLoginSelector = () => {
  const router = useRouter()

  const postLoginCallback = async (
    data: {
      jwt?: string
      authCode?: string
    },
    type: 'auth-code' | 'jwt',
  ) => {
    try {
      const requestBody =
        type === 'auth-code'
          ? { data: { authCode: data.authCode! }, type }
          : { data: { jwt: data.jwt! }, type }

      const { email } = await postAuthByProviderNameOneTap({
        providerName: 'google',
        requestBody,
      })

      if (email) {
        await ANALYTICS.identify(email, {
          name: email,
          email: email,
        })
      }

      setCookie(COOKIE_KEYS.IS_LOGGED_IN, 'true', 30)
      setCookie(COOKIE_KEYS.COMPOSIO_EMAIL, email ?? '', 30)

      if (email) {
        Sentry.setUser({
          email: email,
        })
      }
      router.push(INITIAL_PAGE_AFTER_LOGIN)
      // You can add more logic here, such as redirecting the user or storing the tokens
    } catch {
      toast.error(
        'Some error occurred while trying to log you in. Please try after sometime',
      )
    }
  }

  const login = useGoogleLogin({
    onSuccess: (credentialResponse) => {
      postLoginCallback({ authCode: credentialResponse.code }, 'auth-code')
    },
    onError: noop,
    flow: 'auth-code',
    ux_mode: 'redirect',
    select_account: true,
    redirect_uri: `${getCurrentClientSideHost()}/provider_login/google`,
  })

  return login
}

export const useGithubLoginSelector = () => {
  const currentHost = getCurrentClientSideHost()
  const url = `${currentHost}/provider_login/github`

  return () => {
    router.push(
      `${OpenAPI.BASE}/api/v3/auth/github/login?redirectUri=${encodeURIComponent(url)}`,
    )
  }
}

const GoogleLoginComponent = ({ children }: { children: React.ReactNode }) => {
  return (
    <GoogleOAuthProvider
      clientId={GOOGLE_CLIENT_ID}
      onScriptLoadError={() => {}}
    >
      {children}
    </GoogleOAuthProvider>
  )
}

export default GoogleLoginComponent
